import i18n from '@/helper/i18n';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { getGuestDisplayName } from './guests';
import { paxTypes } from '@/helper/constants/aa-checkout';

dayjs.extend(customParseFormat);
const t = i18n.chain;

function validateDuplicatedByKey(
  guests: AACheckout.Passenger[],
  getKey: (guest: AACheckout.Passenger) => string,
  errorMessage: ({ names }) => string,
) {
  const keys = guests.map(getKey);

  const duplicatedKeys = guests
    .map(getKey)
    .filter((key, i) => key.replaceAll('&', '') && keys.indexOf(key) !== i);

  const invalidPassengers = guests
    .filter((g) => duplicatedKeys.includes(getKey(g)))
    .map(getGuestDisplayName);

  if (invalidPassengers.length > 0) {
    throw new Error(
      errorMessage({
        names: invalidPassengers.join('、'),
      }),
    );
  }
}

function validateDateByField(
  guests: AACheckout.Passenger[],
  field: string,
  errorMessage: ({ names }) => string,
) {
  const dateFormation = 'DD/MM/YYYY';

  const invalidPassengers = guests
    .filter(
      (g, i) =>
        g.formData[field] &&
        !dayjs(g.formData[field], dateFormation, true).isValid(),
    )
    .map(getGuestDisplayName);

  if (invalidPassengers.length > 0) {
    throw new Error(
      errorMessage({
        names: invalidPassengers.join('、'),
      }),
    );
  }
}

function validateDates(guests: AACheckout.Passenger[]) {
  validateDateByField(
    guests,
    'dateOfBirth',
    t.flights.aaCheckout.errors.dateOfBirthError,
  );
  validateDateByField(
    guests,
    'expiryDate',
    t.flights.aaCheckout.errors.expiryDateError,
  );
}

function validateDuplicates(guests: AACheckout.Passenger[]) {
  validateDuplicatedByKey(
    guests,
    (g) => `${g.formData.docNumber}`.toLowerCase(),
    t.flights.aaCheckout.errors.duplicatedTravelDocError,
  );

  validateDuplicatedByKey(
    guests,
    (g) =>
      `${g.formData.familyName}&${g.formData.givenName}&${g.formData.dateOfBirth}`.toLowerCase(),
    t.flights.aaCheckout.errors.duplicatedNameAndDOBError,
  );
}

function validateInfantChildAndNoAdult(guests: AACheckout.Passenger[]) {
  if (
    guests.some(
      (g) =>
        g.subPaxType === paxTypes.child || g.subPaxType === paxTypes.infant,
    ) &&
    !guests.some((g) => g.subPaxType === paxTypes.adult)
  ) {
    throw new Error(t.flights.aaCheckout.errors.infantChildWithNoAdultError);
  }
}

function validateNationality(
  guests: AACheckout.Passenger[],
  countries: Array<{ value: string }>,
) {
  const validCountryCodes = countries.map((country) => country.value);

  const invalidPassengers = guests
    .filter(
      (guest) =>
        guest.formData.nationality &&
        !validCountryCodes.includes(guest.formData.nationality),
    )
    .map(getGuestDisplayName);

  if (invalidPassengers.length > 0) {
    throw new Error(
      t.flights.aaCheckout.errors.invalidNationalityError({
        names: invalidPassengers.join('、'),
      }),
    );
  }
}

export function applyAdditionalValidation(
  guests: AACheckout.Passenger[],
  extendParams: any,
) {
  validateDates(guests);
  validateDuplicates(guests);
  validateInfantChildAndNoAdult(guests);
  validateNationality(guests, extendParams.countries);
}
